import React from "react";
import { Button } from "@/components/ui/button";

interface AppErrorBoundaryState {
  hasError: boolean;
  message: string;
  retryCount: number;
}

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

const MAX_AUTO_RETRY = 2;
const AUTO_RETRY_DELAY = 800;

class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  private windowErrorHandler?: (event: ErrorEvent) => void;
  private unhandledRejectionHandler?: (event: PromiseRejectionEvent) => void;
  private retryTimer?: ReturnType<typeof setTimeout>;

  state: AppErrorBoundaryState = {
    hasError: false,
    message: "",
    retryCount: 0,
  };

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return {
      hasError: true,
      message: error.message || "Unexpected application error",
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[AppErrorBoundary] Render crash:", error, errorInfo);

    // Auto-retry for transient HMR / module errors
    if (this.isTransientError(error.message) && this.state.retryCount < MAX_AUTO_RETRY) {
      this.retryTimer = setTimeout(() => {
        this.setState(prev => ({
          hasError: false,
          message: "",
          retryCount: prev.retryCount + 1,
        }));
      }, AUTO_RETRY_DELAY);
    }
  }

  private extractMessage(input: unknown): string {
    if (!input) return "Unexpected application error";
    if (typeof input === "string") return input;
    if (input instanceof Error) return input.message || "Unexpected application error";
    if (typeof input === "object" && "message" in (input as Record<string, unknown>)) {
      const message = (input as Record<string, unknown>).message;
      return typeof message === "string" ? message : "Unexpected application error";
    }
    return "Unexpected application error";
  }

  private isTransientError(message: string): boolean {
    const n = message.toLowerCase();
    return (
      n.includes("chunkloaderror") ||
      n.includes("loading chunk") ||
      n.includes("failed to fetch dynamically imported module") ||
      n.includes("module script") ||
      n.includes("dynamically imported module") ||
      n.includes("load module")
    );
  }

  private isFatalRuntimeMessage(message: string): boolean {
    const n = message.toLowerCase();
    return (
      n.includes("chunkloaderror") ||
      n.includes("loading chunk") ||
      n.includes("failed to fetch dynamically imported module") ||
      n.includes("module script")
    );
  }

  componentDidMount() {
    this.windowErrorHandler = (event) => {
      const message = this.extractMessage(event.error || event.message);
      console.error("[AppErrorBoundary] Window error:", event.error || event.message);
      if (!this.state.hasError && this.isFatalRuntimeMessage(message)) {
        this.setState({ hasError: true, message });
      }
    };

    this.unhandledRejectionHandler = (event) => {
      const message = this.extractMessage(event.reason);
      console.error("[AppErrorBoundary] Unhandled rejection:", event.reason);
      if (!this.state.hasError && this.isFatalRuntimeMessage(message)) {
        this.setState({ hasError: true, message });
      }
    };

    window.addEventListener("error", this.windowErrorHandler);
    window.addEventListener("unhandledrejection", this.unhandledRejectionHandler);
  }

  componentWillUnmount() {
    if (this.windowErrorHandler) {
      window.removeEventListener("error", this.windowErrorHandler);
    }
    if (this.unhandledRejectionHandler) {
      window.removeEventListener("unhandledrejection", this.unhandledRejectionHandler);
    }
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, message: "", retryCount: 0 });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="w-full max-w-xl rounded-2xl border border-border bg-card p-6 shadow-sm space-y-4">
          <h1 className="text-xl font-semibold">Preview crashed</h1>
          <p className="text-sm text-muted-foreground">
            Something went wrong while rendering the app. Please retry preview.
          </p>
          {this.state.message ? (
            <p className="text-xs text-muted-foreground break-words border border-border rounded-md p-3 bg-muted/50">
              {this.state.message}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={this.handleReload}>
              Reload preview
            </Button>
            <Button type="button" variant="outline" onClick={this.handleReset}>
              Try recover
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;